import { AsyncPipe, JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, isDevMode } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { map } from 'rxjs';

/**
 * The tenant's Auth0 post-login Action refuses unverified emails with
 * `api.access.deny('email_not_verified')`. On a redirect login Auth0 then returns the browser to
 * the callback with `error=access_denied&error_description=email_not_verified`, and
 * `@auth0/auth0-angular` sends the app to its `errorPath` (`/error` in all four apps) and emits the
 * SDK's `GenericError` on `AuthService.error$` with those two values as `.error` / `.error_description`.
 */
const ACCESS_DENIED = 'access_denied';
const EMAIL_NOT_VERIFIED = 'email_not_verified';

/** True only for the exact access_denied + email_not_verified pair; every other value is a generic error. */
function isEmailNotVerifiedError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) {
    return false;
  }
  const { error, error_description } = err as { error?: unknown; error_description?: unknown };
  return error === ACCESS_DENIED && error_description === EMAIL_NOT_VERIFIED;
}

/**
 * The `/error` page, mounted by all four apps (admin, calendar, customer and operator) at `/error`;
 * customer also mounts it at `/server-error`. Two states, both left-aligned in the page column:
 *  - email not verified: Auth0 refused the sign-in, so tell the person to open the verification
 *    link we emailed and offer one action to sign in again;
 *  - generic: everything else (and a direct visit with no auth error) keeps the "We've hit a snag" page.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-server-error',
  imports: [AsyncPipe, JsonPipe, RouterLink],
  templateUrl: './server-error.component.html',
  styleUrl: './server-error.component.scss',
})
export class ServerErrorComponent {
  readonly auth = inject(AuthService);
  readonly isDev = isDevMode();

  /**
   * `error$` replays its last value, so a component created after the SDK has failed the callback
   * still gets the error on first render (no flash of the generic state).
   */
  protected readonly emailNotVerified = toSignal(this.auth.error$.pipe(map(isEmailNotVerifiedError)), {
    initialValue: false,
  });

  /** Primary action of the verify-email state: start a fresh Auth0 redirect login. */
  protected signInAgain(): void {
    this.auth.loginWithRedirect();
  }
}
